/**
 * useCrewPerformance.js — Crew P&L / "money made the company"
 *
 * Answers: how much company gross profit did each crew member generate, using
 * REAL booked numbers:
 *
 *   per job:  profit = revenue − Σ crew payouts − Σ expenses
 *   per crew: Σ ( profit × their payout share )   ... share = theirPay / totalJobPay
 *
 * Payout source = job_earnings_ledger (state IN 'earned','paid' → booked money).
 * We use ONLY the ledger (never also crew_earnings — independent stores; summing
 * both double-counts). The ledger is chosen over the manual crew_earnings grid
 * because (1) it has real coverage across all delivered jobs and (2) it carries
 * `role`, giving an EXACT Originator / Production / Post split rather than a
 * best-effort guess. As the manual grid becomes the payment system of record,
 * revisit this source.
 *
 * Admin-only surface: the ledger endpoint is server-scoped (non-admins see only
 * their own rows), and it exposes everyone's pay — gate the view on view_all_pay.
 *
 * Exports: useCrewPerformance(range) → { crew, clients, departments }
 *   range: 'all' | 'ytd' | 't12m'
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { snakeToCamel } from '../lib/utils';
import { useJobs } from './useJobs';
import { useExpenses } from './useExpenses';
import { useContacts } from './useContacts';

// Pre-deal / dead jobs never made the company money — exclude.
const EXCLUDED_STATUS = ['intake', 'quoted', 'confirmed'];
const isEligible = (job) => {
  const s = (job.status || '').toLowerCase();
  if (!s || s.includes('void')) return false;
  return !EXCLUDED_STATUS.includes(s);
};

// ledger roles → department bucket
const DEPT_OF_ROLE = {
  originator: 'orig', orig: 'orig',
  prod_lead: 'prod', prod_hourly: 'prod', production: 'prod',
  post_lead: 'post', post_hourly: 'post', post_production: 'post',
};
const DEPT_KEYS = ['orig', 'prod', 'post'];
const DEPT_LABEL = { orig: 'Originator', prod: 'Production', post: 'Post' };

function jobDate(job) {
  const d = job.shootDate || job.deliveryDate || job.createdAt;
  return d ? new Date(d) : null;
}
function inRange(job, range) {
  if (range === 'all') return true;
  const d = jobDate(job);
  if (!d || isNaN(d.getTime())) return true; // undated → don't hide it
  const now = new Date();
  if (range === 'ytd') return d.getFullYear() === now.getFullYear();
  if (range === 't12m') {
    const cut = new Date(now);
    cut.setFullYear(cut.getFullYear() - 1);
    return d >= cut;
  }
  return true;
}

export function useCrewPerformance(range = 'all') {
  const { data: jobs = [] } = useJobs();
  const { data: expenses = [] } = useExpenses();
  const { data: contacts = [] } = useContacts();
  // All ledger rows (admins see everyone); realtime-invalidated via this key.
  const { data: ledgerRaw = [] } = useQuery({
    queryKey: ['job-earnings-ledger'],
    queryFn: () => api.get('/job-earnings-ledger'),
  });

  return useMemo(() => {
    const ledger = ledgerRaw.map(snakeToCamel).filter((r) => r.state === 'earned' || r.state === 'paid');
    const eligible = jobs.filter((j) => isEligible(j) && inRange(j, range));
    const jobById = new Map(eligible.map((j) => [j.id, j]));

    // Σ expenses per eligible job (job-linked only; overhead has no job_id)
    const expByJob = new Map();
    for (const e of expenses) {
      if (!e.jobId || !jobById.has(e.jobId)) continue;
      expByJob.set(e.jobId, (expByJob.get(e.jobId) || 0) + (parseFloat(e.amount) || 0));
    }

    // Per job → per person: total pay + per-department pay (from role)
    const payByJob = new Map(); // jobId -> Map(person -> { pay, dept:{orig,prod,post} })
    for (const r of ledger) {
      if (!r.jobId || !jobById.has(r.jobId)) continue;
      const person = (r.person || '').trim();
      if (!person) continue;
      const amt = parseFloat(r.amount) || 0;
      if (!payByJob.has(r.jobId)) payByJob.set(r.jobId, new Map());
      const pm = payByJob.get(r.jobId);
      if (!pm.has(person)) pm.set(person, { pay: 0, dept: { orig: 0, prod: 0, post: 0 } });
      const rec = pm.get(person);
      rec.pay += amt;
      const d = DEPT_OF_ROLE[(r.role || '').toLowerCase()];
      if (d) rec.dept[d] += amt;
    }

    const people = new Map();
    const ensure = (name) => {
      const key = name.toLowerCase();
      if (!people.has(key)) people.set(key, { name, profit: 0, revenue: 0, paid: 0, jobIds: new Set(), byDept: { orig: 0, prod: 0, post: 0 } });
      return people.get(key);
    };
    const deptTotals = { orig: { profit: 0, jobs: new Set() }, prod: { profit: 0, jobs: new Set() }, post: { profit: 0, jobs: new Set() } };

    for (const [jobId, pm] of payByJob) {
      const job = jobById.get(jobId);
      const totalPay = [...pm.values()].reduce((a, r) => a + r.pay, 0);
      if (totalPay <= 0) continue;
      const revenue = parseFloat(job.revenue) || 0;
      const profit = revenue - totalPay - (expByJob.get(jobId) || 0);

      for (const [person, rec] of pm) {
        const share = rec.pay / totalPay;
        const profitCredit = profit * share;
        const acc = ensure(person);
        acc.profit += profitCredit;
        acc.revenue += revenue * share;
        acc.paid += rec.pay;
        acc.jobIds.add(jobId);
        // split this person's profit credit across their departments by pay weight
        for (const d of DEPT_KEYS) {
          if (rec.dept[d] > 0) {
            const dc = profitCredit * (rec.dept[d] / rec.pay);
            acc.byDept[d] += dc;
            deptTotals[d].profit += dc;
            deptTotals[d].jobs.add(jobId);
          }
        }
      }
    }

    const contactByName = new Map(contacts.map((c) => [(c.name || '').toLowerCase(), c]));
    const crew = [...people.values()]
      .map((p) => ({
        ...p,
        jobCount: p.jobIds.size,
        multiple: p.paid > 0 ? p.profit / p.paid : null,
        contact: contactByName.get(p.name.toLowerCase()) || null,
      }))
      .sort((a, b) => b.profit - a.profit);

    const departments = DEPT_KEYS.map((key) => ({
      key,
      label: DEPT_LABEL[key],
      profit: deptTotals[key].profit,
      jobCount: deptTotals[key].jobs.size,
      top: crew
        .filter((p) => p.byDept[key] !== 0)
        .sort((a, b) => b.byDept[key] - a.byDept[key])
        .slice(0, 3)
        .map((p) => ({ name: p.name, amount: p.byDept[key] })),
    }));

    // Clients ranked by booked revenue (separate question from crew profit)
    const clients = contacts
      .filter((c) => c.type === 'client')
      .map((c) => {
        const cJobs = eligible.filter((j) => j.contactId === c.id);
        return { contact: c, name: c.name, revenue: cJobs.reduce((s, j) => s + (parseFloat(j.revenue) || 0), 0), jobCount: cJobs.length };
      })
      .filter((c) => c.revenue > 0)
      .sort((a, b) => b.revenue - a.revenue);

    return { crew, clients, departments };
  }, [jobs, expenses, contacts, ledgerRaw, range]);
}
